import { css } from '@emotion/react';
import CollapsiblePanel from 'athena/components/collapsible-panel';
import t from 'react-translate';
import { MentorshipProgramProfileAnswer } from 'redux/schemas/models/mentoring-program-profile-answers';
import { ProgramLevelQuestion } from 'redux/schemas/models/mentoring-program-profile-questions';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { sortQuestionByQuestionIndex } from 'athena/helpers/utils';
import { PanelStateKey } from 'athena/components/constants';

type ProgramFieldProps = {
  field: MentorshipProgramProfileAnswer;
  role: MentoringProgramRole;
};

const ProgramField = ({ field, role }: ProgramFieldProps) => {
  const { profileQuestion, answer, mentorQuestion } = field || {};
  const { questionList } = (profileQuestion as ProgramLevelQuestion) || {};
  const { questionText } = questionList || {};
  const participantQuestion = (
    role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR
    && !!mentorQuestion
  ) ? mentorQuestion
    : questionText;

  const hasAnswer = answer?.length;

  return hasAnswer ? (
    <div className='mt-4'>
      <div className='text-small gray-2 mb-1'>{participantQuestion}</div>
      <div className='text-regular heading-5 mb-2'>
        {Array.isArray(answer) ? answer.join(', ') : answer}
      </div>
    </div>
  ) : null;
};

type ProgramLevelInfoProps = {
  endRef: (node?: Element | null) => void;
  role: MentoringProgramRole;
  programInfo: MentorshipProgramProfileAnswer[];
};

const styles = css`
  border-radius: 0 0 ${halfSpacing}px ${halfSpacing}px;
`;

const ProgramLevelInfo = ({ endRef, role, programInfo }: ProgramLevelInfoProps) => {
  const panelTitle = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.CONNECTION_DETAILS.PROGRAM_PROFILE.TITLE();

  const hasAnswers = programInfo?.some(({ answer }) => answer?.length);
  const sortedByQuestionIndex = programInfo?.sort(sortQuestionByQuestionIndex);

  return hasAnswers ? (
    <div css={styles} className='px-5 py-4 info-container'>
      <CollapsiblePanel
        isNested
        title={panelTitle}
        iconSize='smallest'
        ariaLabel={panelTitle}
        dataQa={
          config.pendo.athena.mentorshipProgram.participantHome.connectionCard
            .programLevelInfo
        }
        panelState={{
          persistState: true,
          localStorageKey: PanelStateKey.PROGRAM_LEVEL_INFO_STATE,
        }}
      >
        <>
          <div className='mt-4'>
            {sortedByQuestionIndex?.map((field) => (
              <ProgramField key={field.id} field={field} role={role} />
            ))}
          </div>
          <div ref={endRef} />
        </>
      </CollapsiblePanel>
    </div>
  ) : null;
};

export default ProgramLevelInfo;
